import type { Getters, IOrderState } from '@/store/modules/common/orderState';
import OrderState from '@/store/modules/common/orderState';
import type { ISearchParams } from '@/castapi/helpers';
import {
  getErrorMessage,
  getOrdersApi,
  getProductsApi,
  getStripeApi,
  getInvoicesApi,
  getDiscountApi,
} from '@/castapi/helpers';
import type {
  InvoiceDto,
  OrderSearchResponseDto,
  OrganizationWithOwnerDto,
  ProductDto,
  FreeLicenseReasonDto,
} from '@/castapi';
import { AppLogger } from '@/logger';
import type { IActionParams, RootGetters, RootState } from '@/store/modules/index';
import type { ITableParamsState } from '@/store/modules/common/tableParamsState';
import TableParamsState from '@/store/modules/common/tableParamsState';

const order = new OrderState();
const tableParams = new TableParamsState();

const logger = new AppLogger('adminOrders state');

interface IAdminOrderState extends IOrderState, ITableParamsState {
  orders: OrderSearchResponseDto[];
  ordersLoading: boolean;
  ordersLoadError?: string;
  invoices: InvoiceDto[];
  freeLicenseReasons: FreeLicenseReasonDto[];
  freeLicenseReasonsLoading: boolean;
  freeLicenseReasonsLoadError?: string;
}

const initialState = (): IAdminOrderState => ({
  ...order.state(),
  ...tableParams.state(),
  orders: [],
  ordersLoading: false,
  ordersLoadError: undefined,
  productsMap: {},
  productLoadError: undefined,
  productsLoading: false,
  shopProducts: [],
  invoices: [],
  freeLicenseReasons: [],
  freeLicenseReasonsLoading: false,
  freeLicenseReasonsLoadError: undefined,
});

type ActionParams = IActionParams<IAdminOrderState, Getters>;

export default {
  namespaced: true,
  state: initialState,
  mutations: {
    ...order.mutations,
    ...tableParams.mutations,

    RESET_STATE(state: IAdminOrderState): void {
      Object.assign(state, initialState());
    },
    ORDERS_LOADING(state: IAdminOrderState): void {
      state.ordersLoading = true;
      state.ordersLoadError = undefined;
    },
    ORDERS_LOADED(state: IAdminOrderState, orders: OrderSearchResponseDto[]): void {
      state.orders = orders;
      state.ordersLoading = false;
    },
    ORDERS_LOAD_ERROR(state: IAdminOrderState, payload: Error): void {
      state.ordersLoading = false;
      state.ordersLoadError = getErrorMessage(payload);
    },
    SET_RENEW_UPGRADE_PRODUCTS_FOR_FULFILLED_ORDER(state: IAdminOrderState, payload: ProductDto[]): void {
      state.productsLoading = false;
      state.renewUpgrade = {
        ...initialState().renewUpgrade,
        renewProducts: payload,
      };
    },
    INVOICE_SENT(state: IAdminOrderState): void {
      //Needed for subscription
      state.isLoading = false;
    },
    INVOICE_VOIDED(state: IAdminOrderState): void {
      state.isLoading = false;
    },
    INVOICES_LOADED(state: IAdminOrderState, invoices: InvoiceDto[]): void {
      state.isLoading = false;
      state.invoices = [...invoices];
    },
    PAYMENT_LINK_SENT(state: IAdminOrderState): void {
      //Needed for subscription
      state.isLoading = false;
    },
    ORDER_FULFILLED(state: IAdminOrderState): void {
      //needed for subscription
      state.isLoading = false;
    },
    FREE_LICENSE_REASONS_LOADING(state: IAdminOrderState): void {
      state.freeLicenseReasonsLoading = true;
      state.freeLicenseReasonsLoadError = undefined;
    },
    FREE_LICENSE_REASONS_LOADED(state: IAdminOrderState, reasons: FreeLicenseReasonDto[]): void {
      state.freeLicenseReasons = reasons;
      state.freeLicenseReasonsLoading = false;
    },
    FREE_LICENSE_REASONS_LOAD_ERROR(state: IAdminOrderState, payload: Error): void {
      state.freeLicenseReasonsLoading = false;
      state.freeLicenseReasonsLoadError = getErrorMessage(payload);
    },
  },
  actions: {
    ...order.actions,
    ...tableParams.actions,

    resetState({ commit }: ActionParams): void {
      commit('RESET_STATE');
    },
    async loadOrders(
      { commit, rootGetters }: ActionParams,
      payload: ISearchParams & {
        fulfilled?: boolean;
        refunded?: boolean;
        notPaid?: boolean;
        invoiceSent?: boolean;
      },
    ): Promise<void> {
      const { limit, offset, searchText, sortBy, sortDesc, fulfilled, refunded, notPaid, invoiceSent } = payload || {};
      const isEduMode = rootGetters['adminEdu/isEduMode'];
      commit('ORDERS_LOADING');
      try {
        const response = await getOrdersApi(rootGetters['login/accessToken']).ordersControllerGetAllOrders(
          limit,
          offset,
          searchText,
          sortBy,
          sortDesc,
          undefined,
          isEduMode,
          fulfilled,
          refunded,
          notPaid,
          invoiceSent,
        );
        commit('ORDERS_LOADED', response.data);
      } catch (error) {
        commit('ORDERS_LOAD_ERROR', error);
        logger.captureStoreError('loadOrders', error, { limit, offset, searchText, sortBy, sortDesc });
      }
    },

    async getUserUnfinishedOrder(
      { commit, dispatch, getters, rootGetters }: ActionParams,
      userId: number,
    ): Promise<void> {
      try {
        if (!userId) {
          return;
        }
        const token = rootGetters['login/accessToken'];
        if (!token || getters.cartProducts?.length) {
          return;
        }
        if (!getters.allProducts?.length) {
          await dispatch('getProductsFromApi', true);
          return;
        }
        commit('LOADING');
        const response = await getOrdersApi(token).ordersControllerGetUserUnfinishedOrder(userId);
        await dispatch('orderLoaded', response.data);
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('getUserUnfinishedOrder', error, { userId });
      }
    },

    async getRenewUpgradeProductForFulfilledOrder(
      { commit, rootGetters }: ActionParams,
      productId: number,
    ): Promise<void> {
      commit('PRODUCTS_LOADING');
      try {
        const token = rootGetters['login/accessToken'];
        const response = await getProductsApi(token).productsControllerFindOne(productId);
        commit('SET_RENEW_UPGRADE_PRODUCTS_FOR_FULFILLED_ORDER', [response.data]);
      } catch (error) {
        commit('PRODUCTS_LOAD_ERROR', error);
        logger.captureStoreError('getRenewUpgradeProductForFulfilledOrder', error, { productId });
      }
    },

    async sendInvoice({ commit, getters, rootGetters }: ActionParams): Promise<void> {
      const orderId = getters.orderId;
      if (!orderId) {
        return;
      }
      commit('LOADING');
      try {
        const token = rootGetters['login/accessToken'];
        await getStripeApi(token).stripeControllerSendStripeInvoiceForOrder({ orderId });
        commit('INVOICE_SENT');
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('sendInvoice', error);
      }
    },

    async voidInvoice({ commit, getters, rootGetters }: ActionParams): Promise<void> {
      const orderId = getters.orderId;
      if (!orderId) {
        return;
      }
      commit('LOADING');
      try {
        const token = rootGetters['login/accessToken'];
        await getStripeApi(token).stripeControllerVoidStripeInvoiceForOrder({ orderId });
        commit('INVOICE_VOIDED');
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('voidInvoice', error);
      }
    },

    async sendCastInvoiceToPay({ commit, getters, rootGetters }: ActionParams): Promise<void> {
      const orderId = getters.orderId;
      if (!orderId) {
        return;
      }
      commit('LOADING');
      try {
        const token = rootGetters['login/accessToken'];
        await getOrdersApi(token).ordersControllerSendCastInvoiceToPay({ orderId });
        commit('INVOICE_SENT');
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('sendCastInvoiceToPay', error);
      }
    },

    async getOrderInvoices({ commit, getters, rootGetters }: ActionParams): Promise<void> {
      const orderId = getters.orderId;
      if (!orderId) {
        return;
      }
      commit('LOADING');
      try {
        const token = rootGetters['login/accessToken'];
        const response = await getInvoicesApi(token).invoicesControllerGetOrderInvoices(orderId);
        commit('INVOICES_LOADED', response.data);
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('getOrderInvoices', error);
      }
    },

    async fulfillOrder({ commit, getters, rootGetters }: ActionParams): Promise<void> {
      const orderId = getters.orderId;
      if (!orderId) {
        return;
      }
      commit('LOADING');
      try {
        const token = rootGetters['login/accessToken'];
        await getOrdersApi(token).ordersControllerFulfillOrder({ orderId });
        commit('ORDER_FULFILLED');
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('fulfillOrder', error);
      }
    },

    async loadFreeLicenseReasons({ commit, state, rootGetters }: ActionParams): Promise<void> {
      // Skip loading if already have data or currently loading
      if (state.freeLicenseReasons.length || state.freeLicenseReasonsLoading) {
        return;
      }

      commit('FREE_LICENSE_REASONS_LOADING');
      try {
        const token = rootGetters['login/accessToken'];
        const response = await getDiscountApi(token).discountsControllerGetFreeLicenseReasons();
        commit('FREE_LICENSE_REASONS_LOADED', response.data);
      } catch (error) {
        commit('FREE_LICENSE_REASONS_LOAD_ERROR', error);
        logger.captureStoreError('loadFreeLicenseReasons', error);
      }
    },

    async sendPaymentLink({ commit, rootGetters }: ActionParams, { orderId }: { orderId: number }): Promise<void> {
      if (!orderId) {
        return;
      }
      commit('LOADING');
      try {
        const token = rootGetters['login/accessToken'];
        await getOrdersApi(token).ordersControllerSendPaymentLink({ orderId });
        commit('PAYMENT_LINK_SENT');
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('sendPaymentLink', error);
      }
    },
  },

  getters: {
    ...order.getters,
    ...tableParams.getters,
    orders: (state: IAdminOrderState): OrderSearchResponseDto[] => state.orders,
    ordersLoading: (state: IAdminOrderState): boolean => state.ordersLoading,
    ordersLoadError: (state: IAdminOrderState): undefined | string => state.ordersLoadError,
    orderCustomer: (
      state: IAdminOrderState,
      _getters: RootGetters,
      _rootState: RootState,
      rootGetters: {
        'adminOrganizations/organizations': OrganizationWithOwnerDto[];
      },
    ): undefined | OrganizationWithOwnerDto =>
      rootGetters['adminOrganizations/organizations'].find(
        organization => organization.organizationId === state.order?.organizationRef,
      ),
    isCloudOrderValidToFulfil: (state: IAdminOrderState): undefined | boolean =>
      state.order?.isCloud ? state.order?.cloudLicenseCreated : true,
    invoices: (state: IAdminOrderState): InvoiceDto[] => state.invoices,
    freeLicenseReasons: (state: IAdminOrderState): FreeLicenseReasonDto[] => {
      // We filter out duplicates because once duplicates was mentioned in dialog and we didn't found the reason
      const filtered = state.freeLicenseReasons.filter(
        (item, i, self) => i === self.findIndex(r => r.reasonId === item.reasonId),
      );
      if (state.freeLicenseReasons.length > filtered.length) {
        const msg = `[adminOrders] Found duplicates in free license reasons. Original: ${state.freeLicenseReasons.length}, Filtered: ${filtered.length}`;
        console.warn(msg);
      }
      return filtered;
    },
    freeLicenseReasonsLoading: (state: IAdminOrderState): boolean => state.freeLicenseReasonsLoading,
    freeLicenseReasonsLoadError: (state: IAdminOrderState): undefined | string => state.freeLicenseReasonsLoadError,
  },
};
